import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { Link } from '@flipster/i18n/routing';
import CaretDownIcon from '@flipster/react/icon/generated/CaretDownIcon';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';

import { promotionBunja } from '@web/app/_layout/navigation/promotion';

import { NavItem } from './nav-item';
import { GroupNav, MenuNav } from './shared/bunja';
import { menuListCssRaw, visibleHiddenDesktopDownCssRaw } from './shared/css';
import { Dropdown } from './shared/dropdown';
import RedDot from './shared/red-dot';
import { useNav } from './shared/useNav';

export const GnbList = () => {
  const { navigations, onClickLink } = useNav();
  const { promotionAtom } = useBunja(promotionBunja);
  const promotion = useAtomValue(promotionAtom);

  return (
    <ul className={css(hstack.raw({ flex: 1, gap: 8 }), visibleHiddenDesktopDownCssRaw)}>
      {navigations.map((item) => {
        if (item.isHide) return null;
        return (
          <li key={`gnb-${item.label}-${item.type}`}>
            {item.type === 'menu' && <MenuCase item={item} />}
            {item.type === 'group' && <GroupCase item={item} />}
            {item.type === 'link' && (
              <Link href={item.url} key={item.url} onClick={() => onClickLink(item.label)} className={css(menuCssRaw)}>
                <RedDot
                  css={{ smallDesktopDown: { _after: { visibility: 'hidden !important' } } }}
                  redDotKey={item.redDotKey}
                >
                  {item.label}
                </RedDot>
              </Link>
            )}
          </li>
        );
      })}
      {promotion && (
        <li>
          <Link
            href={promotion.url ?? '/'}
            key={promotion.text}
            onClick={() => onClickLink(promotion.text ?? '')}
            className={css(menuCssRaw)}
          >
            <img src={`${promotion.image?.url}?fm=webp&q=80`} alt={promotion.text ?? ''} width={24} height={24} />
            {promotion.text}
          </Link>
        </li>
      )}
    </ul>
  );
};

// 타입 추론 편하게 하기 위해서 따로 컴포넌트 만듬
const MenuCase = ({ item }: { item: MenuNav }) => {
  const { onClickLink } = useNav();

  return (
    <Dropdown
      position='left'
      trigger={
        <div className={css(menuCssRaw)}>
          {item.label}
          <CaretDownIcon size={12} className='dropdown-icon' />
        </div>
      }
      dropdown={
        <ul className={css(menuListCssRaw)}>
          {item.children
            .filter((child) => !child.isHide)
            .map((child) => (
              <li key={`gnb-${item.label}-${child.label}`}>
                <NavItem item={child} onClick={() => onClickLink(item.label, child.label)} />
              </li>
            ))}
        </ul>
      }
    />
  );
};

const GroupCase = ({ item }: { item: GroupNav }) => {
  const { onClickLink } = useNav();

  return (
    <Dropdown
      position='left'
      trigger={
        <div className={css(menuCssRaw)}>
          {item.label}
          <CaretDownIcon size={12} className='dropdown-icon' />
        </div>
      }
      dropdown={
        <>
          {item.children
            .filter((child) => child.children.length && !child.isHide)
            .map((group) => (
              <ul className={css(menuListCssRaw)} key={`gnb-${item.label}-${group.label}`} style={group.style}>
                <li className={css({ textStyle: 'Text/S/Strong', color: 'content.default.level-2', pt: 8, pb: 4 })}>
                  {group.label}
                </li>
                {group.children
                  .filter((child) => !child.isHide)
                  .map((child) => (
                    <li key={`gnb-${item.label}-${group.label}-${child.label}`}>
                      <NavItem item={child} onClick={() => onClickLink(item.label, child.label)} />
                    </li>
                  ))}
              </ul>
            ))}
        </>
      }
    />
  );
};

// h 40 은 버튼과 동일하게 해서 dropdown 위치 동일하게 유지
const menuCssRaw = hstack.raw({
  textStyle: 'Text/M/Strong',
  color: 'content.default.level-4',
  h: 40,
  px: 20,
  gap: 4,
  cursor: 'pointer',
  _hover: {
    color: 'content.accent.info',
  },
  '.dropdown:hover &': {
    color: 'content.accent.info',
  },
});
